import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Enhances a joined-product size picker (blocks/joined-variant-picker.liquid). Handles
 * click-to-deselect, drives this product's own back-in-stock form via `setVariant()`
 * (found via `data-product-id`, since it's rendered by a sibling block), and hides that
 * form whenever the primary's own OOS form is already showing.
 *
 * @typedef {Object} Refs
 * @property {HTMLScriptElement} variantsData
 *
 * @extends {Component<Refs>}
 */
class JoinedVariantPicker extends Component {
  requiredRefs = ['variantsData'];

  /** @type {string | null} */
  #selectedValue = null;

  /** @type {Record<string, any>[] | undefined} */
  #variants;

  /** @type {Record<string, any> | undefined} */
  #selectedVariant;

  connectedCallback() {
    super.connectedCallback();
    // `click`, not `change` — change never fires for a click on an already-checked radio.
    this.addEventListener('click', this.#onClick);

    this.section = this.closest('.shopify-section, dialog');
    // Two listeners for one job. `change` is what the primary's own OOS form reveals itself
    // on (assets/product-oos-form.js), so re-syncing on anything slower leaves both forms
    // on screen until the fetch lands. `variantUpdate` still follows, carrying the resolved
    // variant the fast path has no access to.
    this.section?.addEventListener('change', this.#handlePrimaryChange);
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.#handlePrimaryVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.section?.removeEventListener('change', this.#handlePrimaryChange);
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.#handlePrimaryVariantUpdate);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (!target.closest('[data-joined-product="true"]')) return;

    if (this.#selectedValue === target.value) {
      // Re-clicking the selected size deselects it.
      target.checked = false;
      this.#selectedValue = null;
      this.#updateCheckedState(null);
    } else {
      this.#selectedValue = target.value;
      this.#updateCheckedState(target);
    }
  };

  /** @param {HTMLInputElement | null} selectedInput */
  #updateCheckedState(selectedInput) {
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[type="radio"]'));
    inputs.forEach((input) => {
      const checked = input === selectedInput;
      input.dataset.previousChecked = selectedInput ? input.dataset.currentChecked || 'false' : 'false';
      input.dataset.currentChecked = checked ? 'true' : 'false';
    });

    // undefined on deselect — setVariant(undefined) hides the form.
    this.#selectedVariant = this.#findVariant(selectedInput?.dataset.variantId);
    this.#applySelection();
  }

  /**
   * `variant-picker`, not `joined-variant-picker`: a type selector matches the exact tag, so
   * this picker's own radios are left to `#onClick`.
   * @param {Event} event
   */
  #handlePrimaryChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (!target.closest('variant-picker')) return;

    this.#handlePrimaryVariantUpdate();
  };

  /**
   * Deferred a microtask so `#primaryFormIsShowing()` reads product-oos-form.js's
   * post-update `hidden` state, not a stale one from before it ran. Both handlers share
   * the deferral: listener order between the two components is upgrade order, not ours.
   */
  #handlePrimaryVariantUpdate = () => {
    queueMicrotask(() => this.#applySelection());
  };

  #applySelection() {
    this.#oosForm()?.setVariant(this.#primaryFormIsShowing() ? undefined : this.#selectedVariant);
  }

  /**
   * Scoped to `this.section`, not `document` — a quick-add dialog can have its own OOS
   * forms open alongside the PDP's. No `?? document` fallback: without a section to scope
   * to, searching the whole document is the exact unscoped bug this exists to avoid.
   * @returns {boolean}
   */
  #primaryFormIsShowing() {
    const primaryForm = this.section?.querySelector('product-oos-form[data-track-page-view]');
    return primaryForm ? !primaryForm.hidden : false;
  }

  /**
   * `null` if there's no section to scope to, or the "Product OOS Form" block isn't on
   * the page — callers use `?.`.
   * @returns {(HTMLElement & { setVariant: (variant: Record<string, any> | null | undefined) => void }) | null}
   */
  #oosForm() {
    return this.section?.querySelector(`product-oos-form[data-product-id="${this.dataset.joinedProductId}"]`) ?? null;
  }

  /**
   * @param {string | undefined} variantId
   * @returns {Record<string, any> | undefined}
   */
  #findVariant(variantId) {
    if (!variantId) return undefined;
    this.#variants ??= JSON.parse(this.refs.variantsData.textContent || '[]');
    return this.#variants?.find((variant) => String(variant.id) === variantId);
  }
}

if (!customElements.get('joined-variant-picker')) {
  customElements.define('joined-variant-picker', JoinedVariantPicker);
}
